import React, { useEffect, useRef, useState } from 'react';
import { X, ClipboardList, Loader } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface PlanNameModalProps {
    defaultName: string;
    selectedCount: number;
    isBusy: boolean;
    onClose: () => void;
    onConfirm: (planName: string) => void;
}

/**
 * Liten modal som spør om plannavn før valgte konsoliderings-linjer
 * legges som steg i en ny flytteplan (MoveProcess).
 */
export const PlanNameModal: React.FC<PlanNameModalProps> = ({
    defaultName,
    selectedCount,
    isBusy,
    onClose,
    onConfirm
}) => {
    const { t } = useTranslation();
    const [name, setName] = useState(defaultName);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        // Fokuser og marker hele defaultnavnet så det er lett å overskrive.
        setTimeout(() => {
            inputRef.current?.focus();
            inputRef.current?.select();
        }, 50);
    }, []);

    const handleConfirm = () => {
        if (isBusy) return;
        onConfirm(name.trim() || defaultName);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
                    <div className="flex items-center space-x-2">
                        <ClipboardList className="w-5 h-5 text-purple-600" />
                        <h2 className="text-base font-bold text-gray-800">
                            {t('manager.modules.consolidation.actions.planModalTitle', {}, 'Legg i flytteplan')}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={isBusy}
                        className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
                        aria-label={t('common.buttons.close', {}, 'Lukk')}
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <div className="px-5 py-4 space-y-3">
                    <p className="text-sm text-gray-600">
                        {t(
                            'manager.modules.consolidation.actions.planModalDescription',
                            { '0': selectedCount },
                            `${selectedCount} forslag legges som steg i en ny flytteplan (kladd). Planen kan valideres og utføres fra Flytteplaner-modulen.`
                        )}
                    </p>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                            {t('manager.modules.consolidation.actions.planName', {}, 'Plannavn')}
                        </label>
                        <input
                            ref={inputRef}
                            type="text"
                            value={name}
                            maxLength={100}
                            disabled={isBusy}
                            onChange={(e) => setName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleConfirm(); }}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-50"
                        />
                    </div>
                </div>

                <div className="flex items-center justify-end space-x-2 px-5 py-3 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                    <button
                        onClick={onClose}
                        disabled={isBusy}
                        className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm font-semibold"
                    >
                        {t('common.buttons.cancel', {}, 'Avbryt')}
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={isBusy || name.trim().length === 0}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold flex items-center space-x-2"
                    >
                        {isBusy ? (
                            <>
                                <Loader className="w-4 h-4 animate-spin" />
                                <span>{t('manager.modules.consolidation.actions.executing', {}, 'Utfører...')}</span>
                            </>
                        ) : (
                            <span>{t('manager.modules.consolidation.actions.planConfirm', {}, 'Legg i plan')}</span>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};
